import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/objectWithoutPropertiesLoose";
import _extends from "@babel/runtime/helpers/extends";
import { success, error, abort, isSuccessAction, isErrorAction, isResponseAction, getRequestActionFromResponse } from './actions'; // to support libraries like redux-act and redux-actions

var normalizeActionType = function normalizeActionType(actionType) {
  return typeof actionType === 'function' ? actionType.toString() : actionType;
};

var getEmptyData = function getEmptyData(multiple) {
  return multiple ? [] : null;
};

var operationConfigHasRequestKey = function operationConfigHasRequestKey(config) {
  return typeof config !== 'boolean' && !!config.getRequestKey;
};

var getInitialRequestState = function getInitialRequestState(_ref) {
  var multiple = _ref.multiple,
      operations = _ref.operations;
  return {
    data: getEmptyData(multiple),
    pending: 0,
    error: null,
    operations: operations && Object.entries(operations).reduce(function (prev, _ref2) {
      var _extends2;

      var k = _ref2[0],
          v = _ref2[1];
      return _extends({}, prev, (_extends2 = {}, _extends2[k] = operationConfigHasRequestKey(v) ? {} : {
        error: null,
        pending: 0
      }, _extends2));
    }, {})
  };
};

var getInitialState = function getInitialState(state, reducer, config) {
  if (!reducer) {
    return getInitialRequestState(config);
  }

  return _extends({}, getInitialRequestState(config), reducer(undefined, {}));
};

var defaultConfig = {
  multiple: false,
  getData: function getData(state, action) {
    return action.payload ? action.payload.data : action.data;
  },
  updateData: null,
  getError: function getError(state, action) {
    return action.payload ? action.payload : action.error;
  },
  onRequest: function onRequest(state) {
    return _extends({}, state, {
      pending: state.pending + 1,
      error: null
    });
  },
  onSuccess: function onSuccess(state, action, config) {
    return _extends({}, state, {
      data: config.getData(state, action, config),
      pending: state.pending - 1,
      error: null
    });
  },
  onError: function onError(state, action, config) {
    return _extends({}, state, {
      data: getEmptyData(config.multiple),
      pending: state.pending - 1,
      error: config.getError(state, action, config)
    });
  },
  onAbort: function onAbort(state) {
    return _extends({}, state, {
      pending: state.pending - 1
    });
  },
  resetOn: [],
  operations: null
};
export var createRequestsReducer = function createRequestsReducer(globalConfig) {
  if (globalConfig === void 0) {
    globalConfig = {};
  }

  return function (localConfig, reducer) {
    if (reducer === void 0) {
      reducer = null;
    }

    return function (state, action) {
      var config = _extends({}, defaultConfig, globalConfig, localConfig);

      var nextState = state === undefined ? getInitialState(state, reducer, config) : state;
      var onRequest = config.onRequest,
          onSuccess = config.onSuccess,
          onError = config.onError,
          onAbort = config.onAbort,
          resetOn = config.resetOn,
          getData = config.getData,
          updateData = config.updateData,
          getError = config.getError,
          operations = config.operations,
          actionType = config.actionType;
      var normalizedActionType = normalizeActionType(actionType);

      if (typeof resetOn === 'function' && resetOn(action) || typeof resetOn !== 'function' && resetOn.map(normalizeActionType).includes(action.type)) {
        return _extends({}, getInitialState(state, reducer, config), {
          pending: state.pending
        });
      }

      if (operations && action.type in operations) {
        var _extends3, _extends4;

        var operationConfig = operations[action.type];
        return _extends({}, state, {
          operations: _extends({}, state.operations, (_extends4 = {}, _extends4[action.type] = operationConfigHasRequestKey(operationConfig) ? _extends({}, state.operations[action.type], (_extends3 = {}, _extends3[operationConfig.getRequestKey(action)] = {
            error: null,
            pending: state.operations[action.type][operationConfig.getRequestKey(action)] ? state.operations[action.type][operationConfig.getRequestKey(action)].pending + 1 : 1
          }, _extends3)) : {
            error: null,
            pending: state.operations[action.type].pending + 1
          }, _extends4))
        });
      }

      if (operations && isResponseAction(action) && getRequestActionFromResponse(action).type in operations) {
        var _extends10;

        var requestAction = getRequestActionFromResponse(action);
        var _operationConfig = operations[requestAction.type];

        var _state$operations = state.operations,
            currentOperation = _state$operations[requestAction.type],
            otherOperations = _objectWithoutPropertiesLoose(_state$operations, [requestAction.type]);

        if (isSuccessAction(action)) {
          var _extends6;

          var dataUpdater = null;

          if (typeof _operationConfig === 'boolean' && _operationConfig || typeof _operationConfig !== 'boolean' && typeof _operationConfig.updateData === 'boolean' && _operationConfig.updateData) {
            dataUpdater = updateData || getData;
          } else if (typeof _operationConfig === 'function') {
            dataUpdater = _operationConfig;
          } else if (typeof _operationConfig !== 'boolean' && typeof _operationConfig.updateData !== 'boolean') {
            dataUpdater = _operationConfig.updateData;
          }

          var _getUpdatedCurrentOperation = function _getUpdatedCurrentOperation() {
            if (!operationConfigHasRequestKey(_operationConfig)) {
              return {
                error: null,
                pending: currentOperation.pending - 1
              };
            }

            var currentRequestKey = _operationConfig.getRequestKey(requestAction);

            var operationForRequestKey = currentOperation[currentRequestKey],
                remainingOperations = _objectWithoutPropertiesLoose(currentOperation, [currentRequestKey]);

            if (operationForRequestKey.pending !== 1) {
              var _extends5;

              return _extends({}, remainingOperations, (_extends5 = {}, _extends5[currentRequestKey] = {
                error: null,
                pending: operationForRequestKey.pending - 1
              }, _extends5));
            }

            return remainingOperations;
          };

          return _extends({}, state, {
            data: dataUpdater ? dataUpdater(state, action, config) : state.data,
            operations: _extends({}, otherOperations, (_extends6 = {}, _extends6[requestAction.type] = _getUpdatedCurrentOperation(), _extends6))
          });
        }

        if (isErrorAction(action)) {
          var _extends7, _extends8;

          return _extends({}, state, {
            operations: _extends({}, otherOperations, (_extends8 = {}, _extends8[requestAction.type] = operationConfigHasRequestKey(_operationConfig) ? _extends({}, currentOperation, (_extends7 = {}, _extends7[_operationConfig.getRequestKey(requestAction)] = {
              error: getError(state, action, config),
              pending: currentOperation[_operationConfig.getRequestKey(requestAction)].pending - 1
            }, _extends7)) : {
              error: getError(state, action, config),
              pending: currentOperation.pending - 1
            }, _extends8))
          });
        } // abort case


        var getUpdatedCurrentOperation = function getUpdatedCurrentOperation() {
          if (!operationConfigHasRequestKey(_operationConfig)) {
            return _extends({}, currentOperation, {
              pending: currentOperation.pending - 1
            });
          }

          var currentRequestKey = _operationConfig.getRequestKey(requestAction);

          var operationForRequestKey = currentOperation[currentRequestKey],
              remainingOperations = _objectWithoutPropertiesLoose(currentOperation, [currentRequestKey]);

          if (operationForRequestKey.pending !== 1) {
            var _extends9;

            return _extends({}, remainingOperations, (_extends9 = {}, _extends9[currentRequestKey] = _extends({}, operationForRequestKey, {
              pending: operationForRequestKey.pending - 1
            }), _extends9));
          }

          return remainingOperations;
        };

        return _extends({}, state, {
          operations: _extends({}, otherOperations, (_extends10 = {}, _extends10[requestAction.type] = getUpdatedCurrentOperation(), _extends10))
        });
      }

      switch (action.type) {
        case normalizedActionType:
          return onRequest(state, action, config);

        case success(normalizedActionType):
          return onSuccess(state, action, config);

        case error(normalizedActionType):
          return onError(state, action, config);

        case abort(normalizedActionType):
          return onAbort(state, action, config);

        default:
          return reducer ? reducer(nextState, action) : nextState;
      }
    };
  };
};
export var requestsReducer = createRequestsReducer();